import { createFileRoute, Navigate, Outlet, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { currentUserQueryOptions } from '@/hooks/queries/auth.ts';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar.tsx';
import { AppSidebar } from '@/components/app-sidebar.tsx';
import { Header } from '@/components/header.tsx';

export const Route = createFileRoute('/_logged')({
  component: RouteComponent,
  beforeLoad: async ({ context, location }) => {
    // Prime the user query so protected components read it from cache (no
    // waterfall), and gate on the result. fetchCurrentUser resolves null on a
    // 401; any other failure is treated the same — redirect to /login.
    const user = await context.queryClient
      .ensureQueryData(currentUserQueryOptions())
      .catch((err: unknown) => {
        console.error('Error during User fetch:', err);
        return null;
      });
    if (!user) {
      throw redirect({ to: '/login', search: { redirect: location.pathname } });
    }
    // Authenticated but not a park admin: reject to the login page. The session is
    // left intact (no logout) — the gate is the single source of truth. A staff
    // user (no parkadmin profile) is bounced without the error so /login shows the
    // sudo panel, driven by the cached identity; a genuine non-admin gets the
    // "no_access" message instead.
    if (!user.features.parkadmin) {
      if (user.is_staff) throw redirect({ to: '/login' });
      throw redirect({ to: '/login', search: { error: 'no_access' } });
    }
  },
});

function RouteComponent() {
  // The current-user query polls every 60s (see currentUserQueryOptions). When
  // the session is ended or the identity is revoked externally, fetchCurrentUser
  // resolves null — redirect to /login here so the poll surfaces it as a clean
  // logout, rather than letting useCurrentUser's non-null invariant throw into
  // the error boundary. (Reading the raw query returns null without throwing;
  // children only mount on the non-null branch, so no consumer ever sees null.)
  const { data: user } = useSuspenseQuery(currentUserQueryOptions());
  return user == null ?
      <Navigate to='/login' />
    : <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <Header />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>;
}
